type RouteKeys<TRoutes> = TRoutes[keyof TRoutes];

export const AUXILIARY_ROUTES = {
    forbidden: 'forbidden',
    notFound: 'notFound',
    refresh: 'refresh',
} as const;

export type AuxiliaryRoutes = RouteKeys<typeof AUXILIARY_ROUTES>;

export const AUTH_ROUTES = {
    signIn: 'signIn',
    signUp: 'signUp',
    passwordRecovery: 'passwordRecovery',
    googleAwait: 'googleAwait',
} as const;

export type AuthRoutes = RouteKeys<typeof AUTH_ROUTES>;

export const PRIVATE_ROUTES = {
    redirect: 'redirect',
    onboarding: 'onboarding',
    dashboard: 'dashboard',
    tradingJournal: 'tradingJournal',
    tradingJournalAgent: 'tradingJournalAgent',
    profileManagement: 'profileManagement',
    profileManagementGeneral: 'profileManagementGeneral',
    profileManagementPassword: 'profileManagementPassword',
    profileManagementDevices: 'profileManagementDevices',
    profileManagementBilling: 'profileManagementBilling',
    profileManagementDeleteAccount: 'profileManagementDeleteAccount',
    affiliate: 'affiliate',
    thankYou: 'thankYou',

    // Pattern Studio
    patternStudio: 'patternStudio',
    patternStudioOverview: 'patternStudioOverview',
    patternStudioLive: 'patternStudioLive',
    patternStudioLab: 'patternStudioLab',

    // Classic Tools
    classicTools: 'classicTools',
    lotSizeCalculator: 'lotSizeCalculator',
    newsCalendar: 'newsCalendar',

    // Platform updates
    updates: 'updates',
    updateDetail: 'updateDetail',

    // Checkout
    boostCheckout: 'boostCheckout',
} as const;

export type PrivateRoutes = RouteKeys<typeof PRIVATE_ROUTES>;

// PUBLIC
// auth
export const getSignInPath = (
    action?:
        | 'sign-up-initiated'
        | 'sign-up-completed'
        | 'reset-password-initiated'
        | 'reset-password-completed',
    email?: string,
) => {
    switch (true) {
        case action === 'sign-up-initiated':
            if (!email) {
                throw new Error('[Router]: Email not provided');
            }

            return `/sign-in?action=${action}&email=${email}`;
        case !!action && action !== 'sign-up-initiated':
            return `/sign-in?action=${action}`;
        default:
            return '/sign-in';
    }
};
export const getSignUpPath = () => '/sign-up';
export const getSignUpViaEmailPath = () => '/sign-up/via-email';
export const getPasswordRecoveryPath = () => '/password-recovery';
export const getResetPasswordPath = () => '/password-recovery/reset-password';
export const getGoogleProcessingPath = () => '/google-processing';
// common
export const getMainPath = () => '/';
export const getForbiddenPath = () => '/forbidden';
export const getNotFoundPath = () => '/not-found';

// PRIVATE
export const getOnboardingPath = () => '/onboarding';
export const getDashboardPath = () => '/dashboard';

export const getProfileManagementPath = () => '/profile-management';
export const getProfileManagementGeneralPath = () =>
    '/profile-management/general';
export const getProfileManagementPasswordPath = () =>
    '/profile-management/password';
export const getProfileManagementDevicesPath = () =>
    '/profile-management/devices';
export const getProfileManagementBillingPath = () =>
    '/profile-management/billing';
export const getProfileManagementDeleteAccountPath = () =>
    '/profile-management/delete-account';

export const getTradingJournalPath = () => '/trading-journal';
export const getTradingJournalAgentPath = (conversationId?: string) =>
    conversationId
        ? `/trading-journal/agent/${conversationId}`
        : '/trading-journal/agent';
export const getAffiliatePath = () => '/affiliate';
export const getAffiliateDashboardPath = () => '/affiliate/dashboard';
export const getAffiliateReferralsPath = () => '/affiliate/referrals';
export const getAffiliateReferralDetailPath = (referralId?: number | string) =>
    referralId !== undefined
        ? `/affiliate/referrals/${referralId}`
        : '/affiliate/referrals/:referralId';
export const getAffiliateCommissionsPath = () => '/affiliate/commissions';
export const getAffiliateCustomPlansPath = () => '/affiliate/custom-plans';
export const getPatternStudioPath = () => '/pattern-studio';
export const getPatternStudioOverviewPath = () => '/pattern-studio/overview';
export const getPatternStudioLivePath = () => '/pattern-studio/live';
export const getPatternStudioLabPath = () => '/pattern-studio/lab';
export const getRefreshPath = () => '/refresh';
export const getThankYouPath = () => '/thank-you';
export const getClassicToolsPath = () => '/classic-tools';
export const getLotSizeCalculatorPath = () =>
    '/classic-tools/lot-size-calculator';
export const getNewsCalendarPath = () => '/classic-tools/news-calendar';

// Platform updates
export const getUpdatesPath = () => '/updates';
export const getUpdateDetailPath = (slug?: string) =>
    slug ? `/updates/${slug}` : '/updates/:slug';

// Checkout
export const getBoostCheckoutPath = (pack?: string) =>
    pack ? `/checkout/boost?pack=${pack}` : '/checkout/boost';
